import { ArrowUpRight, Cable, Calculator, MonitorPlay, SatelliteDish, ServerCog, ThermometerSnowflake, Wrench, Network } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useLang } from "@/i18n";

const FIBRE_IMG = "https://images.unsplash.com/photo-1597733336794-12d05021d510?q=80&w=1200&auto=format&fit=crop";
const ROOFTOP_IMG = "https://images.unsplash.com/photo-1785427566919-6fd631a1163e?q=80&w=1200&auto=format&fit=crop";

const MAIL = (topic) => `mailto:vertrieb@satmueller.de?subject=${encodeURIComponent(`Anfrage: ${topic}`)}`;

const CARDS = [
    {
        id: "kopfstationen",
        span: "md:col-span-8",
        bg: "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white",
        tag: { de: "EIGENE FERTIGUNG", en: "IN-HOUSE MANUFACTURING" },
        dark: true,
        img: "/produkte/dh8-basis.jpg",
        icon: ServerCog,
        de: { title: "Eigenfertigung Kabelkopfstationen (DHx-Basis)", sub: "Modular, kundenspezifisch programmiert & bestückt (DH8, DH4, DH2) — 4 Jahre Garantie" },
        en: { title: "In-house cable headends (DHx base)", sub: "Modular, custom-programmed & assembled (DH8, DH4, DH2) — 4-year warranty" },
        badge: "/produkte/garantie-4jahre.webp",
        href: "#kopfstationen",
        testid: "bento-kopfstationen",
    },
    {
        id: "reparatur",
        span: "md:col-span-4",
        bg: "bg-amber-50 border-amber-200 text-slate-900",
        tag: { de: "EXPRESS SERVICE", en: "EXPRESS SERVICE" },
        icon: Wrench,
        de: { title: "48h Reparatur-Service & Pauschalen", sub: "Exklusiver Schnellservice für Grundig & kompatible Kassetten" },
        en: { title: "48h repair service & flat rates", sub: "Exclusive fast service for Grundig & compatible cassettes" },
        href: "#reparatur",
        testid: "bento-reparatur",
    },
    {
        id: "sat-zf",
        span: "md:col-span-4",
        bg: "bg-white",
        tag: { de: "VERTEILTECHNIK", en: "DISTRIBUTION" },
        icon: Network,
        de: { title: "SAT-ZF-Verteiltechnik & Multischalter", sub: "Hochfrequenz-Verteilung für Mehrfamilienhäuser & Großobjekte" },
        en: { title: "SAT-IF distribution & multi-switches", sub: "RF distribution for apartment blocks & large sites" },
        href: "/produkte/sat-zf-verteilsysteme",
        testid: "bento-sat-zf",
    },
    {
        id: "fibre",
        span: "md:col-span-4",
        bg: "bg-white",
        tag: { de: "OPTIK & GLASFASER", en: "OPTICS & FIBRE" },
        icon: Cable,
        de: { title: "Fibre-Glas Empfangssysteme", sub: "Dämpfungsarme optische Signalübertragung über weite Strecken" },
        en: { title: "Fibre optic receive systems", sub: "Low-loss optical signal transmission over long distances" },
        img: FIBRE_IMG,
        href: "/produkte/fibre-glas",
        testid: "bento-fibre",
    },
    {
        id: "antennenheizung",
        span: "md:col-span-4",
        bg: "bg-white",
        tag: { de: "WETTERSCHUTZ", en: "WEATHER PROTECTION" },
        icon: ThermometerSnowflake,
        de: { title: "Professionelle Antennenheizungen", sub: "Automatische Enteisung für Satellitenschüsseln bei Extremwetter" },
        en: { title: "Professional antenna heating", sub: "Automatic de-icing for satellite dishes in extreme weather" },
        img: ROOFTOP_IMG,
        href: "/produkte/antennenheizung",
        testid: "bento-antennenheizung",
    },
    {
        id: "einzelumsetzer",
        span: "md:col-span-4",
        bg: "bg-white",
        tag: { de: "EMPFANGSTECHNIK", en: "RECEPTION TECH" },
        icon: SatelliteDish,
        de: { title: "Einzelumsetzer & LNB-Systeme", sub: "Präzisions-Umsetzer für erstklassige Signalaufbereitung" },
        en: { title: "Transmodulators & LNB systems", sub: "Precision transmodulators for first-class signal processing" },
        href: "/produkte/einzelumsetzer",
        testid: "bento-einzelumsetzer",
    },
    {
        id: "infomedia",
        span: "md:col-span-4",
        bg: "bg-white",
        tag: { de: "MEDIASYSTEME", en: "MEDIA SYSTEMS" },
        icon: MonitorPlay,
        de: { title: "InfoMedia-Player", sub: "Informationskanäle für Hotels, Kliniken & Wohnanlagen" },
        en: { title: "InfoMedia players", sub: "Information channels for hotels, clinics & residences" },
        href: "/produkte/infomedia",
        testid: "bento-infomedia",
    },
    {
        id: "planung",
        span: "md:col-span-4",
        bg: "bg-red-50 border-red-200",
        tag: { de: "INGENIEURLEISTUNG", en: "ENGINEERING" },
        icon: Calculator,
        de: { title: "Berechnung & Netzplanung GA / GGA / BK", sub: "Ingenieurmäßige Pegelberechnung und zukunftsfähige Auslegung" },
        en: { title: "Calculation & network planning CATV / estates", sub: "Engineering-grade level calculation and future-proof design" },
        href: MAIL("Netzplanung GA / GGA / BK"),
        testid: "bento-planung",
    },
];

export default function ProductBento() {
    const { t } = useLang();
    return (
        <section id="produkte" data-testid="produkte" className="scroll-mt-24 py-16 sm:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                <Reveal>
                    <Eyebrow>{t("Produktwelt", "Products")}</Eyebrow>
                    <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 max-w-2xl">
                        {t("Alles für den Empfang. Von der Kopfstation bis zur Antennenheizung.", "Everything for reception. From the headend to antenna heating.")}
                    </h2>
                </Reveal>
                <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-4">
                    {CARDS.map((c, i) => (
                        <Reveal key={c.id} delay={(i % 3) * 0.07} className={`col-span-1 ${c.span}`}>
                            <a
                                href={c.href}
                                data-testid={c.testid}
                                className={`group relative flex h-full min-h-[190px] flex-col justify-between overflow-hidden rounded-xl border border-slate-200 p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${c.bg}`}
                            >
                                {c.img && (
                                    <>
                                        <img
                                            src={c.img}
                                            alt=""
                                            loading="lazy"
                                            className="absolute inset-0 h-full w-full object-cover opacity-25 transition-all duration-500 group-hover:opacity-40 group-hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/30 to-transparent" aria-hidden="true" />
                                    </>
                                )}
                                <div className="relative flex items-start justify-between gap-3">
                                    <span
                                        className={`eyebrow text-[9.5px] px-2 py-1 rounded-sm border ${
                                            c.img
                                                ? "text-white/90 bg-white/10 border-white/25"
                                                : c.dark
                                                ? "text-red-300 bg-red-500/10 border-red-400/30"
                                                : "text-slate-600 bg-slate-100 border-slate-200"
                                        }`}
                                    >
                                        {t(c.tag.de, c.tag.en)}
                                    </span>
                                    <c.icon className={`h-5 w-5 shrink-0 ${c.img ? "text-white/90" : c.dark ? "text-red-300" : "text-red-700"}`} />
                                </div>
                                <div className="relative mt-8">
                                    <h3 className={`font-display text-lg sm:text-xl font-bold leading-snug ${c.img ? "text-white" : ""}`}>
                                        {t(c.de.title, c.en.title)}
                                    </h3>
                                    <p className={`mt-1.5 text-sm leading-relaxed ${c.img ? "text-slate-300" : "text-slate-600"}`}>
                                        {t(c.de.sub, c.en.sub)}
                                    </p>
                                    {c.badge && (
                                        <img
                                            src={c.badge}
                                            alt="4 Jahre Garantie"
                                            loading="lazy"
                                            data-testid="bento-garantie-badge"
                                            className="absolute bottom-0 right-0 h-16 w-auto -rotate-6 drop-shadow-[0_4px_10px_rgba(0,0,0,0.35)]"
                                        />
                                    )}
                                    <span
                                        className={`mt-4 inline-flex items-center gap-1 text-sm font-semibold ${
                                            c.img ? "text-white" : "text-red-700"
                                        }`}
                                    >
                                        {t("Mehr erfahren", "Learn more")}
                                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                    </span>
                                </div>
                            </a>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
